import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSign, Clock, Users, AlertCircle, TrendingUp, Building, ArrowRight } from 'lucide-react';
import { ShareButton } from './ShareAnalysis';

const CostCalculator = () => {
  const [inputs, setInputs] = useState({
    monthlyRevenue: 1000000,
    engineerCount: 5,
    avgSalary: 150000,
    incidentsPerMonth: 3,
    avgResolutionHours: 4,
    impactPercentage: 20,
    slaExposure: 10,
    timeReduction: 70,
  });

  const [savings, setSavings] = useState({
    current: {
      engineering: 0,
      revenue: 0,
      sla: 0,
      total: 0
    },
    withDeltaOps: {
      engineering: 0,
      revenue: 0,
      sla: 0,
      total: 0
    },
    scenarios: [
      {
        name: 'Conservative',
        current: 0,
        improved: 0,
        savings: 0
      },
      {
        name: 'Moderate',
        current: 0,
        improved: 0,
        savings: 0
      },
      {
        name: 'Full Impact',
        current: 0,
        improved: 0,
        savings: 0
      }
    ]
  });

  // Calculate hourly costs and potential savings
  useEffect(() => {
    // Convert any empty string values to 0 for calculations
    const safeInputs = Object.fromEntries(
      Object.entries(inputs).map(([key, value]) => [key, value === '' ? 0 : value])
    );
      
    const hourlyEngineeringCost = (safeInputs.engineerCount * safeInputs.avgSalary) / 2080;
    const hourlyRevenueImpact = (safeInputs.monthlyRevenue / 720) * (safeInputs.impactPercentage / 100); // 720 hours per month
    const hourlySLAPenalty = (safeInputs.monthlyRevenue * (safeInputs.slaExposure / 100)) / 720;

    // Calculate monthly costs for different scenarios
    const calculateScenario = (resolutionHours) => {
      const engineeringCost = hourlyEngineeringCost * resolutionHours * inputs.incidentsPerMonth;
      const revenueImpact = hourlyRevenueImpact * resolutionHours * inputs.incidentsPerMonth;
      const slaPenalty = hourlySLAPenalty * resolutionHours * inputs.incidentsPerMonth;
      
      return {
        engineering: engineeringCost,
        revenue: revenueImpact,
        sla: slaPenalty,
        total: engineeringCost + revenueImpact + slaPenalty
      };
    };

    const currentCosts = calculateScenario(inputs.avgResolutionHours);
    const improvedCosts = calculateScenario(inputs.avgResolutionHours * (1 - inputs.timeReduction / 100)); // User-defined reduction

    // Generate data for different scenarios
    const scenarios = [
      {
        name: 'Conservative',
        current: currentCosts.engineering,
        improved: improvedCosts.engineering,
        savings: currentCosts.engineering - improvedCosts.engineering
      },
      {
        name: 'Moderate',
        current: currentCosts.engineering + currentCosts.revenue * 0.5,
        improved: improvedCosts.engineering + improvedCosts.revenue * 0.5,
        savings: (currentCosts.engineering + currentCosts.revenue * 0.5) - 
                (improvedCosts.engineering + improvedCosts.revenue * 0.5)
      },
      {
        name: 'Full Impact',
        current: currentCosts.total,
        improved: improvedCosts.total,
        savings: currentCosts.total - improvedCosts.total
      }
    ];

    setSavings({
      current: currentCosts,
      withDeltaOps: improvedCosts,
      scenarios
    });
  }, [inputs]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const renderInput = (label, name, value, icon, hint) => {
    const Icon = icon;
    return (
      <div className="relative mb-6 group">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-blue-500" />
          </div>
          <Input
            type="number"
            value={value}
            onChange={(e) => {
              const value = e.target.value;
              // Allow empty string, otherwise parse as float
              const newValue = value === '' ? value : parseFloat(value) || 0;
              setInputs(prev => ({...prev, [name]: newValue}));
            }}
            className="pl-10 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
          />
        </div>
        {hint && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 -bottom-6 bg-gray-800 text-white text-xs px-2 py-1 rounded pointer-events-none">
            {hint}
          </div>
        )}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 shadow-lg rounded-lg border border-gray-100">
          <p className="text-sm font-semibold text-gray-900 mb-2">{label} Scenario</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm font-medium" style={{ 
              color: entry.dataKey === 'current' ? '#1e40af' : '#1d4ed8'
            }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Updated generateROISummary function
  const generateROISummary = (data) => {
    const { 
      inputs, 
      monthlyRevenue, 
      engineerCount, 
      annualSavings, 
      timeReduction, 
      currentResolutionTime, 
      improvedResolutionTime,
      conservative, 
      moderate, 
      fullImpact 
    } = data;

    // Safety check for improvedResolutionTime
    const resolvedImprovedResolutionTime = improvedResolutionTime !== undefined 
      ? improvedResolutionTime.toFixed(1) 
      : 'N/A';

    return `DeltaOps ROI Analysis Summary

Current Situation:
• ${engineerCount} engineers at $${inputs.avgSalary.toLocaleString()}/year
• ${inputs.incidentsPerMonth} incidents per month
• ${currentResolutionTime} hours average resolution time
• $${monthlyRevenue.toLocaleString()} monthly revenue

Potential Impact:
• Annual Savings: $${Math.round(annualSavings).toLocaleString()}
• Time Reduction: ${timeReduction}%
• Incident Resolution Time: ${resolvedImprovedResolutionTime} hours

This includes:
• Engineering time cost savings
• Revenue impact reduction
• SLA/customer satisfaction improvements

Generated via DeltaOps.app/pricing`;
  };

  return (
    <div id="savings" className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Enhanced Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-blue-700 text-sm font-medium mb-4">
            <TrendingUp className="w-4 h-4 mr-2" />
            ROI Calculator
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Calculate Your Cost Savings
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
            Estimate your potential savings by reducing incident resolution time
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Enhanced Input Section */}
          <Card className="lg:col-span-1 bg-white shadow-xl border-0">
            <CardHeader className="border-b border-gray-100 bg-gray-50 rounded-t-xl">
              <div className="flex items-center space-x-2">
                <Building className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-semibold text-gray-900">Company Details</h3>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {/* Time Reduction Slider */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1 flex justify-between">
                  Estimated Time Reduction
                  <span className="text-blue-600">{inputs.timeReduction}%</span>
                </label>
                <div className="relative mt-2">
                  <input
                    type="range"
                    min="10"
                    max="90"
                    value={inputs.timeReduction}
                    onChange={(e) => setInputs(prev => ({...prev, timeReduction: parseInt(e.target.value)}))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  />
                </div>
                <p className="mt-4 text-xs text-gray-500 italic">
                  Adjust the estimated reduction in incident resolution time
                </p>
              </div>

              {/* Disclaimer */}
              <div className="mb-6 p-3 bg-yellow-50 border border-yellow-100 rounded-lg">
                <p className="text-xs text-yellow-800">
                  Note: Savings estimates are based on potential reduction in incident resolution time. 
                  Actual results may vary depending on your team's specific circumstances, implementation, 
                  and incident complexity.
                </p>
              </div>

              {/* Input Fields */}
              <div className="space-y-4">
                {renderInput(
                  'Monthly Revenue', 
                  'monthlyRevenue', 
                  inputs.monthlyRevenue, 
                  DollarSign,
                  'Your company\'s average monthly revenue'
                )}
                {renderInput(
                  'Number of Engineers', 
                  'engineerCount', 
                  inputs.engineerCount, 
                  Users,
                  'Engineers involved in incident response'
                )}
                {renderInput(
                  'Average Engineer Salary', 
                  'avgSalary', 
                  inputs.avgSalary, 
                  DollarSign,
                  'Annual salary including benefits'
                )}
                {renderInput(
                  'Incidents per Month', 
                  'incidentsPerMonth', 
                  inputs.incidentsPerMonth, 
                  AlertCircle,
                  'Average number of production incidents'
                )}
                {renderInput(
                  'Average Resolution Time (hours)', 
                  'avgResolutionHours', 
                  inputs.avgResolutionHours, 
                  Clock,
                  'Time from detection to resolution'
                )}
              </div>
            </CardContent>
          </Card>

          {/* Enhanced Results Section */}
          <Card className="lg:col-span-2 bg-white shadow-xl border-0">
            <CardHeader className="border-b border-gray-100 bg-gray-50 rounded-t-xl">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <h3 className="text-lg font-semibold text-gray-900">Potential Monthly Savings</h3>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {/* Chart Section */}
              <div className="mb-6 sm:mb-8">
                <div className="h-48 sm:h-64 lg:h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={savings.scenarios}
                      margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="name" 
                        stroke="#64748b"
                        tick={{ fill: '#64748b', fontSize: '12px' }}
                        interval={window.innerWidth < 640 ? 1 : 0}
                      />
                      <YAxis 
                        tickFormatter={(value) => formatCurrency(value)}
                        stroke="#64748b"
                        tick={{ fill: '#64748b', fontSize: '12px' }}
                        width={80}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Area 
                        type="monotone" 
                        name="Current Cost" 
                        dataKey="current" 
                        stroke="#94a3b8" 
                        fill="#f1f5f9"
                        strokeWidth={2}
                      />
                      <Area 
                        type="monotone" 
                        name="With DeltaOps" 
                        dataKey="improved" 
                        stroke="#3b82f6" 
                        fill="#dbeafe"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Scenarios Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {savings.scenarios.map((scenario) => (
                  <div 
                    key={scenario.name}
                    className="relative overflow-hidden p-4 sm:p-6 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="absolute top-0 right-0 w-16 sm:w-20 h-16 sm:h-20 transform translate-x-6 -translate-y-6">
                      <div className="absolute inset-0 bg-blue-500 opacity-10 rotate-45"></div>
                    </div>
                    <div className="relative">
                      <div className="text-sm font-medium text-gray-600 mb-2">{scenario.name}</div>
                      <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-1">
                        {formatCurrency(scenario.savings)}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500">monthly savings</div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Share Button */}
              <div className="mt-6 flex justify-end">
                <ShareButton
                  data={{
                    inputs,
                    monthlyRevenue: inputs.monthlyRevenue,
                    engineerCount: inputs.engineerCount,
                    annualSavings: savings.scenarios[2].savings * 12,
                    timeReduction: inputs.timeReduction,
                    currentResolutionTime: inputs.avgResolutionHours,
                    improvedResolutionTime: inputs.avgResolutionHours * (1 - inputs.timeReduction / 100),
                    conservative: savings.scenarios[0],
                    moderate: savings.scenarios[1],
                    fullImpact: savings.scenarios[2]
                  }}
                  generateSummary={(data) => generateROISummary(data)}
                  buttonText="Share Analysis"
                  modalTitle="Share Cost Analysis Results"
                  CTAText="Start Reducing Costs"
                />
              </div>

              {/* Explanation Section */}
              <div className="mt-6 sm:mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">How we calculate this:</h4>
                <ul className="text-xs sm:text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    Engineering cost based on salary and time spent
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    Revenue impact based on service degradation
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    SLA penalties and customer impact included
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CostCalculator;